import React, { useCallback, useEffect, useRef, useState } from 'react';

import { FEEDBACK_QUESTIONS, postSlackMessage, sendAction } from '../../utils/api';

import './Feedback.css';

function Feedback({ handleError, handleFeedback }) {
  const formRef = useRef(null);
  const [hasHover, setHasHover] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stars, setStars] = useState({});
  const [starsHover, setStarsHover] = useState({});

  const handleHover = useCallback((param) => {
    setHasHover(param);
  }, []);

  const handleStarClick = useCallback((param, index) => {
    setStars(prevStars => {
      prevStars[param] = index;

      return {...prevStars};
    });
  }, []);

  const handleStarHover = useCallback((param, index) => {
    setStarsHover(prevStars => {
      prevStars[param] = index;

      return {...prevStars};
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isSubmitting && formRef.current) {
      setIsSubmitting(true);

      const formData = new FormData(formRef.current);
      let data = {};
      let message = '';

      FEEDBACK_QUESTIONS.forEach(question => {
        if (question.type === 'checkbox') {
          if (question.values?.length > 1) {
            const value = question.values[formData.get(question.param) !== null ? 1 : 0];

            if (value) {
              data[question.param] = value;
              message += '*' + question.label + '*\n' + value + '\n\n';
            }
          }
        } else if (question.type === 'multiradio') {
          let submessage = '';

          question.prompts.forEach(prompt => {
            const value = formData.get(prompt.param);

            if (value) {
              data[prompt.param] = value;
              submessage += prompt.label + ' ' + value + '\n';
            }
          });

          if (submessage) {
            message += '*' + question.label + '*\n' + submessage + '\n';
          }
        } else if (question.type === 'stars') {
          if (stars[question.param] !== undefined) {
            const value = question.values[stars[question.param]];

            data[question.param] = value;
            message += '*' + question.label + '*\n' + value + '\n';
          }
        } else {
          const value = formData.get(question.param);

          if (value) {
            data[question.param] = value;
            message += '*' + question.label + '*\n' + value + '\n';
          }
        }
      });

      if (Object.values(data).length) {
        try {
          if (message) {
            await postSlackMessage('*New Feedback Submission*\n\n' + message.replace(/(<([^>]+)>)/gi, ''));
          }
        } catch (e) {
          handleError(e);
        }

        try {
          const response = await sendAction('feedback', null, data);

          if (response !== null) {
            handleFeedback(false);
            setIsSubmitting(false);
          }
        } catch (e) {
          handleError(e);
          setIsSubmitting(false);
        }
      } else {
        handleFeedback(false);
        setIsSubmitting(false);
      }
    }
  };

  const triggerSubmit = useCallback(() => {
    if (formRef.current) {
      formRef.current.requestSubmit();
    }
  }, []);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        handleFeedback(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [handleFeedback]);

  return (
    <div id="feedback-container" className="popup-overlay">
      <div className="popup overflow">
        <div className="popup-title">Help us make the AI Guide more helpful and more fun!</div>
        {/*<p>Please tell us what you think. For assistance please email <a href="mailto:support@campusevolve.ai">support@campusevolve.ai</a>.</p>*/}
        <hr />
        <form ref={formRef} onSubmit={handleSubmit}>
          {FEEDBACK_QUESTIONS.map((question, qi) =>
            <React.Fragment key={qi}>
              {question.divider ? (
                <hr />
              ) : null}
              <div key={question.param} className={'question' + (question.type === 'checkbox' ? ' flex' : '')}>
                <label htmlFor={'feedback_' + question.param} dangerouslySetInnerHTML={{__html: question.label}} />
                {question.sublabel ? (
                  <div className="sublabel">{question.sublabel}</div>
                ) : null}
                {question.type === 'checkbox' ? (
                  <div className="checkbox">
                    <input type="checkbox" id={'feedback_' + question.param} name={question.param} value={question.values?.length > 1 ? question.values[1] : '1'} />
                  </div>
                ) : null}
                {question.type === 'multiradio' ? (
                  <div className="multiradio">
                    <div className="values">
                      <div className="label"></div>
                      {question.values.map((value, index) => 
                        <div key={index} className="value">{value}</div>
                      )}
                    </div>
                    <div className="prompts">
                      {question.prompts.map((prompt, index) =>
                        <div key={index} className="prompt">
                          <div className="label">{prompt.label}</div>
                          {question.values.map((value, index) =>
                            <div key={index} className="value">
                              <input type="radio" name={prompt.param} value={value} />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ) : null}
                {question.type === 'radio' ? (
                  <div className="radios">
                    {question.values.map((value, index) =>
                      <div key={index} className="radio">
                        <input type="radio" name={question.param} value={value} />
                        <span>{value}</span>
                      </div>
                    )}
                  </div>
                ) : null}
                {question.type === 'stars' ? (
                  <div className="stars" onMouseOut={() => handleHover(null)} onMouseOver={() => handleHover(question.param)}>
                    {question.values.map((value, index) =>
                      <img key={index} src={process.env.PUBLIC_URL + '/assets/icons/feedback-star-' + ((hasHover === question.param && starsHover[question.param] !== undefined && starsHover[question.param] >= index) || (hasHover !== question.param && stars[question.param] !== undefined && stars[question.param] >= index) ? 'full' : 'empty') + '.svg'} alt="Star" onClick={() => handleStarClick(question.param, index)} onMouseOver={() => handleStarHover(question.param, index)} />
                    )}
                  </div>
                ) : null}
                {question.type === 'text' ? (
                  <input type="text" id={'feedback_' + question.param} name={question.param} />
                ) : null}
                {question.type === 'textarea' ? (
                  <textarea id={'feedback_' + question.param} name={question.param} placeholder="Type your message..."></textarea>
                ) : null}
              </div>
            </React.Fragment>
          )}
        </form>
        <hr />
        <div>Contact support at <a href="mailto:support@campusevolve.ai">support@campusevolve.ai</a>.</div>
        <hr />
        <div className="actions">
          <div className="action action-secondary" onClick={handleFeedback}>Cancel</div>
          <div className="action action-primary" onClick={triggerSubmit}>Send Feedback</div>
        </div>
      </div>
    </div>
  );
}

export default Feedback;